export type WaitForConditionOptions = {
  /** Initial delay before the second condition check (ms). Default: 100 */
  initialDelay?: number;
  /** Maximum single delay between attempts (ms). Default: 2000 */
  maxDelay?: number;
  /** Total timeout budget (ms). Default: 10000 */
  timeout?: number;
  /** Backoff strategy. Default: 'exponential' */
  strategy?: 'exponential' | 'linear';
  /** Multiplication factor for exponential strategy. Default: 2 */
  factor?: number;
  /** Increment step for linear strategy (ms). Default: 100 */
  step?: number;
  /** If true, rejects on timeout instead of resolving undefined. Default: false */
  throwOnTimeout?: boolean;
};

/**
 * Recursively waits until the provided condition returns a truthy (non-false / non-null / non-undefined) value
 * or the total timeout budget is exhausted.
 *
 * Supports exponential or linear backoff. Delays are capped by maxDelay and total elapsed time is capped by timeout.
 *
 * Example:
 * const value = await waitForCondition(() => document.querySelector('#ready'));
 *
 * @typeParam T The resolved, truthy value returned by the condition.
 * @param condition Function returning a (possibly async) value. Any of: T | false | null | undefined means "not ready yet".
 * @param options Configuration for timing and backoff.
 * @returns Promise resolving with the condition's truthy value, or rejecting / resolving to undefined on timeout.
 */
export const waitForCondition = async <T>(
  condition: () => T | false | null | undefined | Promise<T | false | null | undefined>,
  options: WaitForConditionOptions = {},
): Promise<T | undefined> => {
  const {
    initialDelay = 100,
    maxDelay = 2000,
    timeout = 10_000,
    strategy = 'exponential',
    factor = 2,
    step = 100,
    throwOnTimeout = false,
  } = options;

  const start = Date.now();

  const attempt = async (currentDelay: number): Promise<T | undefined> => {
    const result = await condition();
    if (result !== false && result !== null && result !== undefined) {
      return result as T;
    }

    const elapsed = Date.now() - start;
    if (elapsed >= timeout) {
      if (throwOnTimeout) {
        throw new Error('waitForCondition: timeout exceeded');
      }
      return undefined;
    }

    const nextDelay =
      strategy === 'exponential' ? Math.min(currentDelay * factor, maxDelay) : Math.min(currentDelay + step, maxDelay);

    const remaining = timeout - elapsed;
    const effectiveDelay = Math.min(nextDelay, remaining);

    await new Promise((r) => {
      setTimeout(r, effectiveDelay);
    });
    return attempt(nextDelay);
  };

  return attempt(initialDelay);
};
